import moment from 'moment';

export type RentUpdateNoticeState = 'none' | 'actionable';

export interface ScheduledRentUpdate {
    /** 0 based index of the update, matching `rentalUpdates[].order` on the contract. */
    order: number;
    /** Months elapsed between the contract initial date and the update. */
    monthsFromStart: number;
    /** First day-of-contract of the month the update becomes effective. */
    startDate: moment.Moment;
}

export interface ScheduledRentUpdatesParams {
    initialDate?: moment.MomentInput;
    durationMonths?: number;
    updateFrequency?: number;
}

export interface CalculateRentUpdateNoticeParams extends ScheduledRentUpdatesParams {
    rentalUpdates?: { order?: number; price?: number }[];
    /** Date the contract was loaded into the app, informed by the backend as `createdAt`. */
    contractCreatedAt?: moment.MomentInput;
    actualDate?: moment.MomentInput;
}

/**
 * Scheduled increases of a contract, derived from its configured periodicity:
 * initialDate + updateFrequency, initialDate + 2 * updateFrequency, ... while inside the contract duration.
 */
export const calculateScheduledRentUpdates = ({
    initialDate,
    durationMonths,
    updateFrequency,
}: ScheduledRentUpdatesParams): ScheduledRentUpdate[] => {
    const frequency = Number(updateFrequency);
    const duration = Number(durationMonths);

    if (!initialDate || !Number.isFinite(frequency) || frequency < 1) return [];
    if (!Number.isFinite(duration) || duration < 1) return [];

    const initialDateContract = moment(initialDate);
    if (!initialDateContract.isValid()) return [];

    const finishDateContract = moment(initialDate).add(duration, 'M').subtract(1, 'd');
    const updates: ScheduledRentUpdate[] = [];

    for (let order = 0; ; order += 1) {
        const monthsFromStart = frequency * (order + 1);
        const startDate = moment(initialDateContract).add(monthsFromStart, 'M');
        if (startDate.isAfter(finishDateContract)) break;
        updates.push({ order, monthsFromStart, startDate });
    }

    return updates;
};

const isUpdateAlreadyPriced = (
    rentalUpdates: { order?: number; price?: number }[] | undefined,
    order: number,
): boolean => (rentalUpdates ?? []).some((update) => update.order === order && Number(update.price ?? 0) > 0);

/**
 * A contract loaded during the very month of one of its increases is loaded with the price that increase
 * already produced, so that increase has nothing left to inform: it counts as resolved whatever its amount.
 */
const isUpdateResolvedByContractLoad = (
    contractCreatedAt: moment.MomentInput | undefined,
    updateStartDate: moment.Moment,
): boolean => {
    if (!contractCreatedAt) return false;

    const loadedMonth = moment(contractCreatedAt);
    if (!loadedMonth.isValid()) return false;

    return loadedMonth.isSame(updateStartDate, 'month') && loadedMonth.isSame(updateStartDate, 'year');
};

/**
 * State of the rent increase notice for a periodic contract. The notice is a reminder of a pending action,
 * never an announcement of a future date: a scheduled increase stays completely silent until its own month
 * starts, and turns itself off as soon as that increase is resolved.
 *
 * - `actionable`  from day 1 of the increase month (whole month long), while the increase is unresolved.
 * - `none`        every other case: no increase due this month, or the due increase is already resolved.
 *
 * An increase is resolved once its amount was informed, or when the contract itself was loaded on the month
 * of that increase. A resolved increase never hands the notice over to the following one: the schedule moves
 * on by itself when the next increase month arrives.
 */
export const calculateRentUpdateNoticeState = ({
    initialDate,
    durationMonths,
    updateFrequency,
    rentalUpdates,
    contractCreatedAt,
    actualDate,
}: CalculateRentUpdateNoticeParams): RentUpdateNoticeState => {
    const scheduledUpdates = calculateScheduledRentUpdates({ initialDate, durationMonths, updateFrequency });
    if (!scheduledUpdates.length) return 'none';

    const currentMonth = moment(actualDate).startOf('month');
    if (!currentMonth.isValid()) return 'none';

    const nextUpdate = scheduledUpdates.find(
        (update) => !moment(update.startDate).startOf('month').isBefore(currentMonth),
    );
    if (!nextUpdate) return 'none';

    const isDueThisMonth = moment(nextUpdate.startDate).startOf('month').isSame(currentMonth);
    if (!isDueThisMonth) return 'none';
    if (isUpdateResolvedByContractLoad(contractCreatedAt, nextUpdate.startDate)) return 'none';
    if (isUpdateAlreadyPriced(rentalUpdates, nextUpdate.order)) return 'none';

    return 'actionable';
};

export interface FollowUpRentUpdateNoticeParams extends CalculateRentUpdateNoticeParams {
    /** `true` when the contract has a configured update periodicity (new rental law). */
    isPeriodicContract?: boolean;
    /** Legacy flag informed by the backend, the only notice driver for non periodic contracts. */
    upgradeable?: boolean;
    /** 1 based month of the period the follow-up card represents (1 = January). */
    followUpMonth?: number;
    /** Year of the period the follow-up card represents. */
    followUpYear?: number;
}

export interface FollowUpRentUpdateNotice {
    /** Increase state of the contract, only calculated for the card of the current month. */
    state: RentUpdateNoticeState;
    /** The card represents a period already gone by. */
    isOldFollowUp: boolean;
    /** The card represents the period currently being managed. */
    isCurrentMonthFollowUp: boolean;
    /** Current month card of a periodic contract with scheduled increases: the only card driven by the schedule. */
    isCurrentPeriodicFollowUp: boolean;
    /** Whether the notice (icon + tooltip) has to be rendered at all. */
    showUpdateNotice: boolean;
    /** Whether the notice may offer the action that updates the rental price. */
    showUpdateAction: boolean;
}

/**
 * Single decision about the rent increase notice of one follow-up card.
 *
 * A card is driven by the contract periodic schedule only when it is the card of the current month; old cards and
 * every non periodic contract keep the legacy `upgradeable` notice, which never offers the update action. On the
 * current month card the schedule is the only source of truth, so an increase whose amount was already informed no
 * longer shows a notice even if the backend still flags it as upgradeable; future cards of a scheduled contract
 * show no notice at all, since their increase is announced on the current month card.
 */
export const calculateFollowUpRentUpdateNotice = ({
    isPeriodicContract,
    upgradeable,
    followUpMonth,
    followUpYear,
    actualDate,
    ...contract
}: FollowUpRentUpdateNoticeParams): FollowUpRentUpdateNotice => {
    const today = moment(actualDate);
    const currentMonth = today.month() + 1;
    const currentYear = today.year();
    const month = followUpMonth ?? currentMonth;
    const year = followUpYear ?? currentYear;

    const isOldFollowUp = year < currentYear || (year === currentYear && month < currentMonth);
    const isCurrentMonthFollowUp = year === currentYear && month === currentMonth;

    const hasScheduledUpdates = calculateScheduledRentUpdates(contract).length > 0;
    const isScheduledPeriodicContract = Boolean(isPeriodicContract) && hasScheduledUpdates;
    const isCurrentPeriodicFollowUp = isScheduledPeriodicContract && isCurrentMonthFollowUp;
    // A future card of a scheduled contract can never be the card of an increase: that notice belongs to the card
    // of the current month, so a stale `upgradeable` flag must not resurrect the legacy notice ahead of time.
    const isFuturePeriodicFollowUp = isScheduledPeriodicContract && !isCurrentMonthFollowUp && !isOldFollowUp;

    const state = isCurrentPeriodicFollowUp ? calculateRentUpdateNoticeState({ ...contract, actualDate }) : 'none';

    return {
        state,
        isOldFollowUp,
        isCurrentMonthFollowUp,
        isCurrentPeriodicFollowUp,
        // On a scheduled contract the schedule is the only source of truth for increases, and its
        // notice lives on the current month card. A past card must never announce an update: the
        // legacy `upgradeable` flag can still be true for a past boundary month (e.g. when the
        // schedule price was informed late), and showing it there is exactly the stale arrow that
        // confused past arrears cards with an actionable increase.
        showUpdateNotice: isCurrentPeriodicFollowUp
            ? state !== 'none'
            : isScheduledPeriodicContract
            ? false
            : Boolean(upgradeable),
        showUpdateAction: state === 'actionable',
    };
};
